interface SSECacheLifecycleApi<TData> {
  cacheDataLoaded: Promise<{ data: TData }>;
  cacheEntryRemoved: Promise<void>;
  updateCachedData: (updater: (draft: TData) => void) => void;
}

interface SSECacheHandlerConfig<TData, TMessage> {
  getSSEUrl: (data: TData) => string | null;
  updateCache: (draft: TData, message: TMessage) => void;
}

const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;

export const createSSECacheHandler = <TData, TMessage>(config: SSECacheHandlerConfig<TData, TMessage>) => {
  return async (
    _arg: unknown,
    { cacheDataLoaded, cacheEntryRemoved, updateCachedData }: SSECacheLifecycleApi<TData>,
  ): Promise<void> => {
    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let failedAttempts = 0;
    let stopped = false;

    const stop = (): void => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      eventSource?.close();
      eventSource = null;
    };

    const connect = (url: string): void => {
      if (stopped) return;

      const source = new EventSource(url);
      eventSource = source;

      source.addEventListener('open', () => {
        failedAttempts = 0;
      });

      source.addEventListener('message', (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data) as TMessage;
          updateCachedData((draft) => {
            config.updateCache(draft, message);
          });
        } catch {
          // Ignore malformed messages
        }
      });

      source.addEventListener('error', () => {
        // An 'error' with readyState CONNECTING just means the browser is already retrying on its
        // own. Closing there is what used to strand the stream: one blip — a pod restart, a proxy
        // idle timeout — and updates stopped for good, leaving a progress bar frozen until reload.
        // Only take over once the browser has genuinely given up (CLOSED).
        if (stopped || source.readyState !== EventSource.CLOSED) return;

        source.close();
        eventSource = null;
        failedAttempts += 1;
        const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** (failedAttempts - 1), RECONNECT_MAX_DELAY_MS);
        reconnectTimer = setTimeout(() => connect(url), delay);
      });
    };

    try {
      const { data } = await cacheDataLoaded;

      const url = config.getSSEUrl(data);
      if (!url) return;

      connect(url);
    } catch {
      // cacheDataLoaded rejected — entry removed before data loaded
    }

    await cacheEntryRemoved;
    stop();
  };
};
